import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { MessageService } from '@core/errors/messages-service';
import { Segment } from '@core/interfaces';
import { fileToBase64 } from '@core/utils/file-converter';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { TranslateService } from '@ngx-translate/core';
import compress from 'browser-image-compression';
import { merge } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { FcCompanyInputComponent } from '../../../../../../core/components/fc-input/search-one/fc-company-input/fc-company-input.component';
import { DialogSchedulingSuggestionsComponent } from '../../../dialog-scheduling-suggestions/dialog-scheduling-suggestions.component';


@Component({
  selector: 'tab-general',
  templateUrl: './tab-general.component.html',
  styleUrls: ['./tab-general.component.scss'],
})
export class TabGeneralComponent implements OnInit {

  @Input() public maintenanceForm: FormGroup;
  @Input() public problems = [];
  @Input() public selectedSegment: Segment;
  @Input() public validation = [];

  public isLoading = false;
  public scheduleSuggestionsFormArray: FormArray;

  public readonly allowedExtensions = [
    'csv',
    'txt',
    'jpg',
    'jpeg',
    'png',
    'jfif',
    'heic',
    'webp',
    'mp4',
    'pdf',
    'doc',
    'xls',
    'ppt',
    'docx',
    'xlsx',
    'pptx',
    'kml',
    'dwg',
    'zip',
    'xml'
  ];

  @ViewChild('attachmentsDiv', { static: true })
  private readonly attachmentsDiv: ElementRef<HTMLDivElement>;

  @ViewChild('companyInput', { static: true })
  private readonly companyInput: FcCompanyInputComponent;

  public isProblemRequired = this.authenticationService.hasAccountPreference({
    property: 'openMaintenance',
    subProperty: 'featureProblemRequired',
    expectedValue: true,
    defaultIfUndefined: false
  });

  public maintenanceNumberGeneratorType = this.authenticationService.getAccountPreference({
    property: 'openMaintenance',
    subProperty: 'maintenanceNumberGenerator',
    defaultIfNull: 'sequential'
  });

  constructor(
    public readonly matDialog: MatDialog,
    private readonly translate: TranslateService,
    private readonly messageService: MessageService,
    private readonly progressBar: FuseProgressBarService,
    private readonly authenticationService: AuthenticationService,
  ) {}

  ngOnInit(): void {
    const locationControl = this.maintenanceForm.get('location');
    const addressControl = this.maintenanceForm.get('address');

    merge(locationControl.valueChanges, addressControl.valueChanges)
      .pipe(debounceTime(500))
      .subscribe(() => {
        this.companyInput.resetSearch();
      });
  }

  openSchedulingSuggestionsDialog(): void {
    this.matDialog.open(DialogSchedulingSuggestionsComponent, {
      panelClass: ['scheduling-suggestions'],
      width: '553px',
      disableClose: true,
      data: this.maintenanceForm.get('scheduleSuggestions')
    });
  }

  removeAttachment(event: Event, index: number): void {
    event.stopPropagation();
    this.maintenanceForm.get('attachments').value.splice(index, 1);
  }

  handleDropFile(event: DragEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.onFileChange(Array.from(event.dataTransfer.files || []));
  }

  handleFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.onFileChange(Array.from(input.files || []));
    const { files } = new DataTransfer();
    input.files = files;
  }

  async onFileChange(files: File[]): Promise<void> {
    this.isLoading = true;
    this.progressBar.show();

    const MAX_SIZE = 2 * 1024 * 1024;

    const ATTACHMENT_LIMIT = 10;

    for (const file of files) {
      const hash = `${file.size}_${file.name}`;
      const extension = file.name.split('.').pop();
      const isImage = file.type.startsWith('image/');
      const alreadyAdded = this.maintenanceForm.get('attachments').value.some((attachment) => attachment.hash === hash);
      if (alreadyAdded) {
        this.messageService.warn(this.translate.instant('MAINTENANCES.CREATE_DIALOG.FILE_DUPLICATED'), {
          fileName: file.name
        });
        continue;
      }
      const extensionNotAllowed = !this.allowedExtensions.includes(extension.toLowerCase());
      if (extensionNotAllowed) {
        this.messageService.warn(this.translate.instant('MAINTENANCES.CREATE_DIALOG.EXTENSION_INVALID'), {
          fileName: file.name
        });
        continue;
      }

      const compressedFile = isImage ? await compress(file, { maxSizeMB: MAX_SIZE, maxWidthOrHeight: 1920 }) : file;
      const thumb = isImage ? await compress(compressedFile, { maxWidthOrHeight: 256 }) : null;
      const isTooBig = compressedFile.size > MAX_SIZE;
      if (isTooBig) {
        this.messageService.warn(this.translate.instant('MAINTENANCES.CREATE_DIALOG.FILE_TOO_BIG'));
        continue;
      }

      const attachmentsLimit = this.maintenanceForm.get('attachments').value.length >= ATTACHMENT_LIMIT;
      if (attachmentsLimit) {
        this.messageService.warn(this.translate.instant('MAINTENANCES.CREATE_DIALOG.LIMIT_REACHED'));
        continue;
      }

      this.maintenanceForm.get('attachments').value.push({
        hash,
        thumb: isImage ? await fileToBase64(thumb) : null,
        file: compressedFile,
        filename: file.name,
        size: compressedFile.size,
        type: file.type
      });
    }

    this.attachmentsDiv.nativeElement.classList.remove('drag-over');

    this.isLoading = false;
    this.progressBar.hide();
  }
}
